import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { ToastContainer } from "react-toastify";

import Login from "./components/login.component";
import IndexPage from "./pages/index.page";
import theme from "./utils/theme";
import { 
  selectLoggedIn, 
  selectProfileInfo, 
  selectFactoryId,
  selectOrganisationId 
} from "./selectors/login.selector";
import { loginSuccess } from "./reducers/login.reducer";
import Factories from "./pages/factory.page";
import RequiredPasswordUpdate from "./components/requiredPasswordUpdate.component";
import FaqPage from "./pages/faq.page";

// Component to handle organization-based routing for root path
const RootRedirect = () => {
  const organisationId = useSelector(selectOrganisationId);
  const factoryId = useSelector(selectFactoryId);
  
  // Access Control: if factoryId is present, redirect to that specific factory
  if (factoryId) {
    return <Navigate to={`/factories/${factoryId}`} replace />;
  }
  
  // For TraceDemoRef org, redirect to /todo
  // For other orgs, redirect to /suppliers
  const isTraceOrg = organisationId === "68939387ff6a36dce8d43148";
  return <Navigate to={isTraceOrg ? "/todo" : "/suppliers"} replace />;
};

function App() {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectLoggedIn);
  const userInfo = useSelector(selectProfileInfo);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const storedState = localStorage.getItem("loginState");
    if (storedState) {
      const state = JSON.parse(storedState);
      if (state && state._id && state.token) {
        dispatch(loginSuccess(state));
      }
    }
    setIsInitialized(true);
  }, [dispatch]);

  // Don't render routes until we've checked localStorage
  if (!isInitialized) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
          >
            Loading...
          </div>
        </CssBaseline>
      </ThemeProvider>
    );
  }

  return (
    <>
      <ToastContainer position="bottom-left" theme="dark" />
      
      <ThemeProvider theme={theme}>
        <CssBaseline>
          <Router>
            <Routes>
              {/* Root path with smart redirect */}
              <Route
                path="/"
                element={
                  !isLoggedIn ? <Login /> : <RootRedirect />
                }
              />
              
              {/* Factory routes */}
              <Route
                path="/factories"
                element={!isLoggedIn ? <Navigate to="/" replace /> : <Factories />}
              />
              <Route
                path="/factories/*"
                element={!isLoggedIn ? <Navigate to="/" replace /> : <IndexPage />}
              />
              
              {/* FAQ route */}
              <Route
                path="/faq"
                element={!isLoggedIn ? <Navigate to="/" replace /> : <FaqPage />}
              />
              
              {/* Catch-all route with password update check */}
              <Route
                path="*"
                element={
                  isLoggedIn && userInfo && !userInfo.updatedPassword ? (
                    <RequiredPasswordUpdate />
                  ) : isLoggedIn ? (
                    <IndexPage />
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />
            </Routes>
          </Router>
        </CssBaseline>
      </ThemeProvider>
    </>
  );
}

export default App;